/**
 * OffboardingDetailPage — Issue #363.
 *
 * 红线 (AI GUIDE §6):
 *   - 详情仅展示 employeeId / userId / mode / 状态 / 时间 / 交接清单；
 *     不展示姓名、手机号、邮箱、薪资明细。
 *   - 交接条目的状态变更走 generated api-client；version 乐观锁冲突时
 *     页面提示并保留按钮可重试。
 *   - 最终审核操作受 hr_offboarding:payroll_review 权限保护，按钮在前端按
 *     权限隐藏，后端再保护一道。
 */
import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Card } from '@/components/ui/card';
import { useAuth } from '@/hooks/useAuth';
import { hasPermission, HR_PERMISSIONS } from '@/lib/hrPermissions';
import {
  HR_OFFBOARDING_CASE_STATUS_LABELS,
  HR_OFFBOARDING_HANDOFF_STATUS_LABELS,
  HR_OFFBOARDING_HANDOFF_TYPE_LABELS,
  HR_OFFBOARDING_MODE_LABELS,
  HR_OFFBOARDING_PAYROLL_HOLD_LABELS,
  useHrOffboardingDetail,
  useHrOffboardingMutations,
  type HrOffboardingHandoffItemDto,
} from '@/hooks/useHrOffboarding';

function fmtDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleString('zh-CN');
}

export default function OffboardingDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { permissions } = useAuth();
  const canRead = hasPermission(permissions, HR_PERMISSIONS.OFFBOARDING_READ);
  const canWrite = hasPermission(permissions, HR_PERMISSIONS.OFFBOARDING_WRITE);
  const canReview = hasPermission(
    permissions,
    HR_PERMISSIONS.OFFBOARDING_REVIEW,
  );
  const canPayroll = hasPermission(
    permissions,
    HR_PERMISSIONS.OFFBOARDING_PAYROLL_REVIEW,
  );

  const { detail, events, loading, error, fetchDetail, fetchEvents } =
    useHrOffboardingDetail();
  const {
    updateHandoffItem,
    submitFinalReview,
    recordFinalReview,
    cancelCase,
    loading: mutating,
    error: mutationError,
  } = useHrOffboardingMutations();

  useEffect(() => {
    if (!canRead || !id) return;
    void fetchDetail(id);
    void fetchEvents(id);
  }, [canRead, id, fetchDetail, fetchEvents]);

  const reloaded = useMemo(() => fetchDetail.bind(null, id ?? ''), [fetchDetail, id]);

  if (!canRead) return <AccessDenied resource="员工离职详情" />;
  if (!detail || !id) {
    return (
      <div className="p-6 text-sm text-slate-500">
        {loading ? '正在加载…' : error?.message ?? '加载中…'}
      </div>
    );
  }

  async function onUpdateItem(
    item: HrOffboardingHandoffItemDto,
    nextStatus: HrOffboardingHandoffItemDto['status'],
  ) {
    const result = await updateHandoffItem(id, item.id, {
      expectedVersion: item.version,
      status: nextStatus,
    });
    if (result) await reloaded();
  }

  async function onSubmitForReview() {
    if (!detail) return;
    const result = await submitFinalReview(id, {
      expectedVersion: detail.version,
    });
    if (result) {
      await reloaded();
      await fetchEvents(id);
    }
  }

  async function onFinalReview(decision: 'release' | 'retain') {
    if (!detail) return;
    const result = await recordFinalReview(id, {
      expectedVersion: detail.version,
      decision,
    });
    if (result) {
      await reloaded();
      await fetchEvents(id);
    }
  }

  async function onCancel() {
    if (!detail) return;
    const result = await cancelCase(id, { expectedVersion: detail.version });
    if (result) {
      await reloaded();
      await fetchEvents(id);
    }
  }

  const requiredItems = detail.items.filter((it) => it.required);
  const optionalItems = detail.items.filter((it) => !it.required);

  return (
    <div className="space-y-4 p-6">
      <header className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="text-xl font-semibold">离职案件详情</h1>
          <p className="font-mono text-xs text-slate-500">{detail.id}</p>
        </div>
        <Link
          href="/hr/offboarding"
          className="text-sm text-blue-600 hover:underline"
        >
          ← 返回列表
        </Link>
      </header>

      {(error || mutationError) ? (
        <Card className="border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error?.message ?? mutationError?.message}
        </Card>
      ) : null}

      <Card className="p-4">
        <dl className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <dt className="text-slate-500">员工 ID</dt>
            <dd className="font-mono">{detail.employeeId}</dd>
          </div>
          <div>
            <dt className="text-slate-500">登录账号</dt>
            <dd className="font-mono">{detail.userId ?? '—'}</dd>
          </div>
          <div>
            <dt className="text-slate-500">模式</dt>
            <dd>{HR_OFFBOARDING_MODE_LABELS[detail.mode]}</dd>
          </div>
          <div>
            <dt className="text-slate-500">案件状态</dt>
            <dd>{HR_OFFBOARDING_CASE_STATUS_LABELS[detail.status]}</dd>
          </div>
          <div>
            <dt className="text-slate-500">生效时间</dt>
            <dd>{fmtDateTime(detail.effectiveAt)}</dd>
          </div>
          <div>
            <dt className="text-slate-500">权限已回收</dt>
            <dd>
              {detail.permissionRevokedAt
                ? fmtDateTime(detail.permissionRevokedAt)
                : '尚未执行'}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">登录冻结</dt>
            <dd>
              {detail.loginFrozenAt ? fmtDateTime(detail.loginFrozenAt) : '—'}
            </dd>
          </div>
          <div>
            <dt className="text-slate-500">薪资状态</dt>
            <dd>{HR_OFFBOARDING_PAYROLL_HOLD_LABELS[detail.payrollHoldStatus]}</dd>
          </div>
          <div className="col-span-2">
            <dt className="text-slate-500">离职原因 (已脱敏)</dt>
            <dd>{detail.reason ?? '—'}</dd>
          </div>
          {detail.finalReviewDecision ? (
            <div className="col-span-2">
              <dt className="text-slate-500">终审</dt>
              <dd>
                {detail.finalReviewDecision === 'release'
                  ? '允许发放'
                  : '终审扣留'}
                {' · '}
                {fmtDateTime(detail.finalReviewDecidedAt)}
              </dd>
            </div>
          ) : null}
        </dl>
      </Card>

      <Card className="p-4">
        <h2 className="mb-3 text-base font-medium">必备交接清单</h2>
        <HandoffTable
          items={requiredItems}
          canWrite={canWrite}
          mutating={mutating}
          onUpdate={onUpdateItem}
        />
      </Card>

      {optionalItems.length > 0 ? (
        <Card className="p-4">
          <h2 className="mb-3 text-base font-medium">附加交接条目</h2>
          <HandoffTable
            items={optionalItems}
            canWrite={canWrite}
            mutating={mutating}
            onUpdate={onUpdateItem}
          />
        </Card>
      ) : null}

      <Card className="p-4">
        <h2 className="mb-3 text-base font-medium">流程操作</h2>
        <div className="flex flex-wrap gap-2">
          {canReview &&
          (detail.status === 'in_progress' || detail.status === 'blocked') ? (
            <button
              type="button"
              className="rounded border border-slate-300 px-3 py-1 text-sm hover:bg-muted disabled:opacity-50"
              disabled={mutating}
              onClick={onSubmitForReview}
            >
              提交终审
            </button>
          ) : null}
          {canPayroll && detail.status === 'pending_final_review' ? (
            <>
              <button
                type="button"
                className="rounded border border-emerald-300 bg-emerald-50 px-3 py-1 text-sm text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                disabled={mutating}
                onClick={() => onFinalReview('release')}
              >
                终审：允许发放
              </button>
              <button
                type="button"
                className="rounded border border-amber-300 bg-amber-50 px-3 py-1 text-sm text-amber-800 hover:bg-amber-100 disabled:opacity-50"
                disabled={mutating}
                onClick={() => onFinalReview('retain')}
              >
                终审：扣留
              </button>
            </>
          ) : null}
          {canWrite &&
          (detail.status === 'draft' ||
            detail.status === 'scheduled' ||
            detail.status === 'in_progress' ||
            detail.status === 'pending_final_review' ||
            detail.status === 'blocked') ? (
            <button
              type="button"
              className="rounded border border-rose-300 bg-rose-50 px-3 py-1 text-sm text-rose-700 hover:bg-rose-100 disabled:opacity-50"
              disabled={mutating}
              onClick={onCancel}
            >
              取消案件
            </button>
          ) : null}
        </div>
      </Card>

      <Card className="p-4">
        <h2 className="mb-3 text-base font-medium">审计事件</h2>
        <ul className="space-y-2 text-xs text-slate-600">
          {events.map((e) => (
            <li key={e.id} className="border-b border-slate-100 pb-1">
              <span className="font-mono text-[11px] text-slate-500">
                {fmtDateTime(e.createdAt)}
              </span>
              {' · '}
              <span className="font-medium">{e.action}</span>
              {e.fromStatus || e.toStatus ? (
                <span className="ml-1 text-slate-500">
                  ({e.fromStatus ?? '∅'} → {e.toStatus ?? '∅'})
                </span>
              ) : null}
              {e.reason ? (
                <span className="ml-1 text-slate-500">· {e.reason}</span>
              ) : null}
            </li>
          ))}
          {events.length === 0 ? (
            <li className="text-slate-400">暂无事件记录</li>
          ) : null}
        </ul>
      </Card>
    </div>
  );
}

interface HandoffTableProps {
  items: HrOffboardingHandoffItemDto[];
  canWrite: boolean;
  mutating: boolean;
  onUpdate: (
    item: HrOffboardingHandoffItemDto,
    nextStatus: HrOffboardingHandoffItemDto['status'],
  ) => void | Promise<void>;
}

function HandoffTable({
  items,
  canWrite,
  mutating,
  onUpdate,
}: HandoffTableProps) {
  if (items.length === 0) {
    return <div className="text-sm text-slate-400">暂无条目</div>;
  }
  return (
    <table className="w-full table-fixed text-sm">
      <thead className="bg-slate-50 text-left text-slate-600">
        <tr>
          <th className="w-40 px-3 py-2">类型 / 标题</th>
          <th className="w-28 px-3 py-2">状态</th>
          <th className="w-32 px-3 py-2">完成时间</th>
          <th className="px-3 py-2">操作</th>
        </tr>
      </thead>
      <tbody>
        {items.map((it) => (
          <tr key={it.id} className="border-t border-slate-100">
            <td className="px-3 py-2">
              <div className="text-xs text-slate-500">
                {HR_OFFBOARDING_HANDOFF_TYPE_LABELS[it.type]}
              </div>
              <div>{it.title}</div>
            </td>
            <td className="px-3 py-2">
              {HR_OFFBOARDING_HANDOFF_STATUS_LABELS[it.status]}
            </td>
            <td className="px-3 py-2 text-xs">
              {it.completedAt
                ? new Date(it.completedAt).toLocaleString('zh-CN')
                : it.waivedAt
                  ? `豁免：${new Date(it.waivedAt).toLocaleString('zh-CN')}`
                  : '—'}
            </td>
            <td className="px-3 py-2">
              {canWrite &&
              it.status !== 'completed' &&
              it.status !== 'waived' ? (
                <div className="flex flex-wrap gap-1">
                  {it.status === 'pending' ? (
                    <button
                      type="button"
                      className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
                      disabled={mutating}
                      onClick={() => onUpdate(it, 'in_progress')}
                    >
                      标记进行中
                    </button>
                  ) : null}
                  <button
                    type="button"
                    className="rounded border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700 disabled:opacity-50"
                    disabled={mutating}
                    onClick={() => onUpdate(it, 'completed')}
                  >
                    完成
                  </button>
                  <button
                    type="button"
                    className="rounded border border-slate-300 px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50"
                    disabled={mutating}
                    onClick={() => onUpdate(it, 'waived')}
                  >
                    豁免
                  </button>
                  {it.status !== 'blocked' ? (
                    <button
                      type="button"
                      className="rounded border border-amber-300 bg-amber-50 px-2 py-0.5 text-xs text-amber-700 disabled:opacity-50"
                      disabled={mutating}
                      onClick={() => onUpdate(it, 'blocked')}
                    >
                      阻塞
                    </button>
                  ) : null}
                </div>
              ) : (
                <span className="text-xs text-slate-400">—</span>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}
